import { useState, useCallback, useEffect, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';

export interface UseResizablePanelOptions {
  defaultWidth: number;
  minWidth: number;
  maxWidthPercent: number; // Maximum width as percentage of viewport (e.g., 40 for 40%)
  storageKey?: string; // Optional key for localStorage persistence
  onWidthChange?: (width: number) => void; // Callback during resize (real-time updates)
  disabled?: boolean; // Disable resize functionality (e.g., for touch devices)
  opened?: boolean; // Panel opened state (triggers sync from localStorage)
}

export interface UseResizablePanelReturn {
  width: number;
  isResizing: boolean;
  handleMouseDown: (e: ReactMouseEvent) => void;
}

/**
 * Custom hook for managing resizable panel behavior
 * Handles drag-to-resize functionality with constraints and optional persistence
 */
export const useResizablePanel = ({
  defaultWidth,
  minWidth,
  maxWidthPercent,
  storageKey,
  onWidthChange,
  disabled = false,
  opened = true,
}: UseResizablePanelOptions): UseResizablePanelReturn => {
  // Load initial width from localStorage if available (used only once in useState/useRef)
  const getInitialWidth = () => {
    if (storageKey) {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsedWidth = parseInt(stored, 10);
        if (!isNaN(parsedWidth)) {
          return Math.max(
            minWidth,
            Math.min(parsedWidth, (window.innerWidth * maxWidthPercent) / 100)
          );
        }
      }
    }
    return defaultWidth;
  };

  const [initialWidth] = useState(getInitialWidth);
  const [width, setWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);
  // Track latest width synchronously (eliminates race condition in handleMouseUp)
  const widthRef = useRef(initialWidth);

  // Sync width from localStorage when panel opens
  // This ensures panels with the same storageKey (same mode) share width
  useEffect(() => {
    // Only sync when panel opens (not during resize or re-renders)
    if (!opened || isResizing) {
      return;
    }

    // Read from localStorage and update width if different
    if (storageKey) {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsedWidth = parseInt(stored, 10);
        if (!isNaN(parsedWidth)) {
          const syncedWidth = Math.max(
            minWidth,
            Math.min(parsedWidth, (window.innerWidth * maxWidthPercent) / 100)
          );
          // Update both ref and state to avoid stale closures
          setWidth((prevWidth) => {
            if (syncedWidth !== prevWidth) {
              widthRef.current = syncedWidth;
              onWidthChange?.(syncedWidth);
              return syncedWidth;
            }
            return prevWidth;
          });
        }
      }
    }
  }, [
    opened,
    storageKey,
    minWidth,
    maxWidthPercent,
    onWidthChange,
    isResizing,
  ]);

  // Calculate maximum width based on viewport
  const getMaxWidth = useCallback(() => {
    return (window.innerWidth * maxWidthPercent) / 100;
  }, [maxWidthPercent]);

  // Handle mouse down on resize handle (no-op if disabled)
  const handleMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width;
    },
    [width, disabled]
  );

  // Handle mouse move during resize
  const handleMouseMove = useCallback(
    (e: globalThis.MouseEvent) => {
      if (disabled || !isResizing) return;

      const deltaX = startXRef.current - e.clientX; // Inverted because panel is on right
      const newWidth = startWidthRef.current + deltaX;

      // Apply constraints
      const constrainedWidth = Math.max(
        minWidth,
        Math.min(newWidth, getMaxWidth())
      );

      // Update both ref (sync) and state (async for UI)
      widthRef.current = constrainedWidth;
      setWidth(constrainedWidth);
    },
    [isResizing, minWidth, getMaxWidth, disabled]
  );

  // Handle mouse up to end resize
  const handleMouseUp = useCallback(() => {
    if (disabled || !isResizing) return;

    setIsResizing(false);

    // Persist to localStorage using ref (eliminates race condition with async setState)
    if (storageKey) {
      localStorage.setItem(storageKey, widthRef.current.toString());
    }

    // Sync width to consumers (e.g. Zustand store) only on mouseup
    // Avoids cascading re-renders (MapLibre viewport recalc) on every mousemove frame
    onWidthChange?.(widthRef.current);
  }, [isResizing, storageKey, disabled, onWidthChange]);

  // Add/remove global event listeners for drag (skip if disabled)
  useEffect(() => {
    if (disabled || !isResizing) return undefined;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp, disabled]);

  // Update width on window resize to maintain constraints (skip if disabled)
  useEffect(() => {
    if (disabled) return undefined;

    const handleWindowResize = () => {
      const maxWidth = getMaxWidth();
      if (width > maxWidth) {
        widthRef.current = maxWidth;
        setWidth(maxWidth);
        onWidthChange?.(maxWidth); // Sync to Zustand store
      }
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [width, getMaxWidth, onWidthChange, disabled]);

  return {
    width,
    isResizing,
    handleMouseDown,
  };
};
